import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { rbac } from '@dha-nexus/shared';
import { buildUatFeedbackReportPath } from '../briefings/reportEntryLinks.js';
import {
  clearPendingFeedbackMutation,
  feedbackMutationSignature,
  getPendingFeedbackMutation,
  pendingFeedbackMutation,
  shouldRetainFeedbackMutation,
} from '../feedbackMarkup/feedbackIdempotency.js';
import {
  Drawer,
  DrawerForm,
  DrawerFormActions,
  EmptyState,
  ErrorState,
  FormField,
  GaugeCard,
  InlineScoreCell,
  KpiHeroBand,
  LoadingSkeleton,
  PageHeader,
  Select,
  StaggerReveal,
  StatusChip,
  StatusDonutMini,
  Textarea,
  bandTone,
  deriveKpiTone,
} from '../../components/index.js';
import '../feedback/feedbackOps.css';
import './uat.redesign.css';

function scoreTimestamp(survey) {
  const raw = survey?.submittedAt || survey?.createdAt || null;
  if (!raw) return 0;
  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDateTime(value, fallback = 'Not recorded') {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return parsed.toLocaleString();
}

function personLabel(person, fallback = 'Not assigned') {
  if (!person) return fallback;
  const parts = [person.displayName || person.id, person.role].filter(Boolean);
  return parts.length ? parts.join(' · ') : fallback;
}

function participantSummary(session) {
  const participants = Array.isArray(session?.participants) ? session.participants : [];
  if (participants.length) {
    return participants.map((participant) => personLabel(participant, participant.id)).join('; ');
  }
  const fallbackIds = Array.isArray(session?.participantUserIds) ? session.participantUserIds : [];
  return fallbackIds.length ? fallbackIds.join(', ') : 'No participants recorded.';
}

function issuesSummary(session) {
  const issues = Array.isArray(session?.issuesEncountered) ? session.issuesEncountered : [];
  if (issues.length) return issues.join(' • ');
  if (Number(session?.blockerCount || 0) > 0) {
    return `${session.blockerCount} issues recorded.`;
  }
  return 'No open issues recorded.';
}

function sessionWindowLabel(session) {
  return `Started ${formatDateTime(session?.startedAt)} · Ended ${formatDateTime(
    session?.endedAt,
    'In progress',
  )}`;
}

function responseRatioLabel(summaryData) {
  return `${summaryData?.satisfiedResponseCount ?? 0} of ${summaryData?.satisfactionResponseCount ?? 0} responses scored 4+`;
}

function targetStatusLabel(summaryData) {
  const targetPercent = Number(summaryData?.targetPercent ?? 80);
  const satisfactionPercent = Number(summaryData?.satisfactionPercent ?? 0);
  const status =
    summaryData?.targetStatus ||
    (satisfactionPercent > targetPercent
      ? 'meets_target'
      : satisfactionPercent === targetPercent
        ? 'near_target'
        : 'below_target');

  if (status === 'meets_target') return 'Meets target';
  if (status === 'near_target') return 'Near target';
  return 'Below target';
}

// The six survey dimensions the page collects through the follow-up drawer but
// previously discarded on read. Rendered as 0–5 InlineScoreCell bars.
const SURVEY_DIMENSIONS = [
  { key: 'overallScore', label: 'Overall satisfaction', hint: 'Composite experience' },
  { key: 'navigationEaseScore', label: 'Navigation ease', hint: 'Wayfinding & layout' },
  { key: 'workflowCompletionScore', label: 'Workflow completion', hint: 'Task throughput' },
  { key: 'dataConfidenceScore', label: 'Confidence in data', hint: 'Data-trust posture' },
  { key: 'reportingUsefulnessScore', label: 'Reporting usefulness', hint: 'Briefing output' },
  { key: 'aiUsefulnessScore', label: 'AI usefulness', hint: 'Copilot assistance' },
];

// 5-point scale: >=4 good (sage), >=3 mixed (amber), else brick.
const DIMENSION_THRESHOLDS = { good: 4, warn: 3 };

function averageDimension(surveyRows, key) {
  const values = surveyRows
    .map((survey) => Number(survey?.[key]))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 5);
  if (!values.length) return null;
  const total = values.reduce((sum, value) => sum + value, 0);
  return {
    average: total / values.length,
    count: values.length,
  };
}

function cohortSatisfactionPercent(cohort) {
  const responses = Number(cohort?.responseCount ?? 0);
  if (!responses) return null;
  return Math.round((Number(cohort?.satisfiedResponseCount ?? 0) / responses) * 100);
}

function cohortCompletionPercent(cohort) {
  const sessions = Number(cohort?.sessionCount ?? 0);
  if (!sessions) return 0;
  return Math.round((Number(cohort?.completedSessionCount ?? 0) / sessions) * 100);
}

const SURVEY_SCORE_OPTIONS = [
  { value: '1', label: '1 - Very low' },
  { value: '2', label: '2 - Low' },
  { value: '3', label: '3 - Mixed' },
  { value: '4', label: '4 - Strong' },
  { value: '5', label: '5 - Very strong' },
];

const SURVEY_INTENT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No' },
];

function createSurveyDraft(session, recordedSurvey = null) {
  const score = (value) => (value == null ? '' : String(value));
  return {
    sessionId: session?.id || '',
    overallScore: score(recordedSurvey?.overallScore),
    navigationEaseScore: score(recordedSurvey?.navigationEaseScore),
    workflowCompletionScore: score(recordedSurvey?.workflowCompletionScore),
    dataConfidenceScore: score(recordedSurvey?.dataConfidenceScore),
    reportingUsefulnessScore: score(recordedSurvey?.reportingUsefulnessScore),
    aiUsefulnessScore: score(recordedSurvey?.aiUsefulnessScore),
    operationalUseIntent: recordedSurvey?.operationalUseIntent || '',
    biggestBlocker: recordedSurvey?.biggestBlocker || '',
    mostValuableFeature: recordedSurvey?.mostValuableFeature || '',
    comments: recordedSurvey?.comments || '',
  };
}

function hasCompleteRecordedSurvey(survey) {
  return Boolean(
    survey &&
      [
        'overallScore',
        'navigationEaseScore',
        'workflowCompletionScore',
        'dataConfidenceScore',
        'reportingUsefulnessScore',
        'aiUsefulnessScore',
        'operationalUseIntent',
        'biggestBlocker',
        'mostValuableFeature',
        'comments',
      ].every((field) => String(survey[field] ?? '').trim()),
  );
}

function validateSurveyDraft(draft) {
  const errors = {};
  if (!draft?.overallScore) errors.overallScore = 'Overall satisfaction is required.';
  if (!draft?.navigationEaseScore) errors.navigationEaseScore = 'Navigation ease is required.';
  if (!draft?.workflowCompletionScore)
    errors.workflowCompletionScore = 'Workflow completion is required.';
  if (!draft?.dataConfidenceScore) errors.dataConfidenceScore = 'Confidence in data is required.';
  if (!draft?.reportingUsefulnessScore)
    errors.reportingUsefulnessScore = 'Reporting usefulness is required.';
  if (!draft?.aiUsefulnessScore) errors.aiUsefulnessScore = 'AI usefulness is required.';
  if (!draft?.operationalUseIntent)
    errors.operationalUseIntent = 'Operational-use intent is required.';
  if (!String(draft?.biggestBlocker || '').trim())
    errors.biggestBlocker = 'Biggest blocker is required.';
  if (!String(draft?.mostValuableFeature || '').trim())
    errors.mostValuableFeature = 'Most valuable feature is required.';
  if (!String(draft?.comments || '').trim()) errors.comments = 'Free-text comments are required.';
  return errors;
}

export function UatDashboardPage() {
  usePageMetadata({
    title: 'UAT Dashboard',
    subtitle:
      'Cohort readiness, satisfaction trend, and onboarding progress toward adoption proof.',
  });
  const summary = useApi('/uat/satisfaction-summary');
  const { roles = [], user } = useCurrentUser();
  const actorUserId = user?.id;
  const canViewGuideResults = roles.includes(rbac.ROLE_IDS.SYSTEM_ADMIN);
  const sessions = useApi('/uat/sessions');
  const surveys = useApi('/uat/satisfaction');
  const onboarding = useApi('/onboarding/checklists');
  const mutation = useMutation();
  // AC-UAT-1.1 / §10: ?view=pulse renders the compact cohort-vs-80%-line view.
  const [searchParams, setSearchParams] = useSearchParams();
  const isPulseView = searchParams.get('view') === 'pulse';

  function setDashboardView(nextView) {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'pulse') next.set('view', 'pulse');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  }
  const [actionNotice, setActionNotice] = useState(null);
  // Ambiguous-outcome fencing (Round 3): operation+resource → { key, payload }.
  // sessionStorage (via feedbackIdempotency) is the durable copy when an actor
  // id is available; this state covers the current render pass.
  const [unresolvedOps, setUnresolvedOps] = useState({});
  const [staleRefresh, setStaleRefresh] = useState(false);
  const [surveySession, setSurveySession] = useState(null);
  const [surveyDraft, setSurveyDraft] = useState(() => createSurveyDraft());
  const [prefilledSurvey, setPrefilledSurvey] = useState(null);
  const [surveyErrors, setSurveyErrors] = useState({});

  function openSurveyDrawer(session) {
    setActionNotice(null);
    const unresolvedSurvey = unresolvedFor('uat-survey', session.id);
    const latestCandidate = surveyBySessionId[session.id]?.latestSurvey || null;
    const latestSurvey = hasCompleteRecordedSurvey(latestCandidate) ? latestCandidate : null;
    const sourceSurvey = unresolvedSurvey?.payload || latestSurvey;
    setSurveySession(session);
    setSurveyDraft(createSurveyDraft(session, sourceSurvey));
    setPrefilledSurvey(unresolvedSurvey ? null : latestSurvey);
    setSurveyErrors({});
  }

  function closeSurveyDrawer() {
    if (mutation.running) return;
    setSurveySession(null);
    setSurveyDraft(createSurveyDraft());
    setPrefilledSurvey(null);
    setSurveyErrors({});
  }

  function updateSurveyField(field, value) {
    setSurveyDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setSurveyErrors((current) => {
      if (!current?.[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  // Round 3 (AR2-M06 / AC10 / AC11 / AC24): every mutation runs behind a
  // retained UUID Idempotency-Key. An ambiguous outcome (timeout, reset,
  // ambiguous 5xx) fences the operation behind an explicit "Resolve previous
  // submission" state that replays the ORIGINAL payload with the ORIGINAL key
  // until the server resolves it — edited data can never mint a second logical
  // mutation, and a refresh failure after a commit is a separate stale-data
  // warning, never a mutation failure.
  function unresolvedOpId(operation, resourceId) {
    return `${operation}:${resourceId}`;
  }

  function unresolvedFor(operation, resourceId) {
    if (!resourceId) return null;
    return (
      unresolvedOps[unresolvedOpId(operation, resourceId)] ||
      getPendingFeedbackMutation({ actorUserId, operation, resourceId }) ||
      null
    );
  }

  function clearUnresolved(operation, resourceId) {
    clearPendingFeedbackMutation({ actorUserId, operation, resourceId });
    setUnresolvedOps((current) => {
      if (!current[unresolvedOpId(operation, resourceId)]) return current;
      const next = { ...current };
      delete next[unresolvedOpId(operation, resourceId)];
      return next;
    });
  }

  // Post-commit refreshes go through api.get + setData rather than the hooks'
  // refresh(): the hook swallows failures AND clears data to null, which would
  // replace the whole dashboard (and the committed-success notice) with the
  // error state — exactly what AC11 forbids. This path keeps the last-good
  // data on failure and reports it through the stale-data warning instead.
  async function safeRefresh(envelope, path) {
    try {
      const response = await api.get(path);
      envelope.setData(response);
      return true;
    } catch {
      return false;
    }
  }

  async function runGovernedMutation({ operation, resourceId, payload, send, successNotice, refreshes }) {
    const existing = unresolvedFor(operation, resourceId);
    let key;
    let effectivePayload;
    if (existing?.key) {
      // Resolve mode: replay the original request verbatim.
      key = existing.key;
      effectivePayload = existing.payload ?? payload;
    } else {
      effectivePayload = payload;
      const pending = pendingFeedbackMutation({
        actorUserId,
        operation,
        resourceId,
        signature: feedbackMutationSignature({ operation, resourceId, payload }),
        payload,
      });
      key = pending.key;
    }
    setActionNotice(null);
    let response;
    try {
      response = await mutation.run(() =>
        send(effectivePayload, { headers: { 'Idempotency-Key': key } }),
      );
    } catch (error) {
      if (shouldRetainFeedbackMutation(error)) {
        setUnresolvedOps((current) => ({
          ...current,
          [unresolvedOpId(operation, resourceId)]: { key, payload: effectivePayload },
        }));
        setActionNotice({
          tone: 'red',
          title: "Your last submission couldn't be confirmed.",
          message:
            'Nothing was saved twice. Use Resolve previous submission to finish the original request — it replays safely.',
        });
      } else {
        // The server proved the operation did not (and will not) commit.
        clearUnresolved(operation, resourceId);
        setActionNotice({
          tone: 'red',
          title: 'Could not update the UAT dashboard.',
          message: error?.message || 'Unknown error',
        });
      }
      return null;
    }
    // Committed: settle the operation and report success BEFORE refreshing.
    clearUnresolved(operation, resourceId);
    setActionNotice(successNotice(response));
    setStaleRefresh(false);
    const refreshedOk = await refreshes();
    if (!refreshedOk) setStaleRefresh(true);
    return response;
  }

  async function markComplete(checklist) {
    await runGovernedMutation({
      operation: 'onboarding-complete',
      resourceId: checklist.id,
      payload: { status: 'complete', completionPercent: 100 },
      send: (body, options) => api.patch(`/onboarding/checklists/${checklist.id}`, body, options),
      successNotice: () => ({
        tone: 'green',
        title: 'Onboarding checklist updated',
        message: `${checklist.persona || checklist.id} onboarding checklist marked complete.`,
      }),
      refreshes: async () => {
        const results = await Promise.all([
          safeRefresh(onboarding, '/onboarding/checklists'),
          safeRefresh(summary, '/uat/satisfaction-summary'),
        ]);
        return results.every(Boolean);
      },
    });
  }

  async function refreshUatPosture() {
    const results = await Promise.all([
      safeRefresh(summary, '/uat/satisfaction-summary'),
      safeRefresh(sessions, '/uat/sessions'),
      safeRefresh(surveys, '/uat/satisfaction'),
    ]);
    return results.every(Boolean);
  }

  async function retryStaleRefresh() {
    setStaleRefresh(false);
    const results = await Promise.all([
      safeRefresh(summary, '/uat/satisfaction-summary'),
      safeRefresh(sessions, '/uat/sessions'),
      safeRefresh(surveys, '/uat/satisfaction'),
      safeRefresh(onboarding, '/onboarding/checklists'),
    ]);
    if (!results.every(Boolean)) setStaleRefresh(true);
  }

  async function markSessionComplete(session) {
    await runGovernedMutation({
      operation: 'uat-session-complete',
      resourceId: session.id,
      payload: { status: 'complete', completionPercent: 100 },
      send: (body, options) => api.patch(`/uat/sessions/${session.id}`, body, options),
      successNotice: () => ({
        tone: 'green',
        title: 'UAT session updated',
        message: `${session.scenarioName || session.scenarioId || session.id} was marked complete.`,
      }),
      refreshes: refreshUatPosture,
    });
  }

  async function submitFollowUpSurvey() {
    if (!surveySession) return;
    const unresolvedSurvey = unresolvedFor('uat-survey', surveySession.id);
    if (!unresolvedSurvey) {
      const nextErrors = validateSurveyDraft(surveyDraft);
      if (Object.keys(nextErrors).length) {
        setSurveyErrors(nextErrors);
        return;
      }
    }
    const payload = unresolvedSurvey?.payload ?? {
      sessionId: surveyDraft.sessionId,
      overallScore: Number(surveyDraft.overallScore),
      navigationEaseScore: Number(surveyDraft.navigationEaseScore),
      workflowCompletionScore: Number(surveyDraft.workflowCompletionScore),
      dataConfidenceScore: Number(surveyDraft.dataConfidenceScore),
      reportingUsefulnessScore: Number(surveyDraft.reportingUsefulnessScore),
      aiUsefulnessScore: Number(surveyDraft.aiUsefulnessScore),
      operationalUseIntent: surveyDraft.operationalUseIntent,
      biggestBlocker: surveyDraft.biggestBlocker.trim(),
      mostValuableFeature: surveyDraft.mostValuableFeature.trim(),
      comments: surveyDraft.comments.trim(),
    };
    const completedSession = surveySession;
    const response = await runGovernedMutation({
      operation: 'uat-survey',
      resourceId: surveySession.id,
      payload,
      send: (body, options) => api.post('/uat/satisfaction', body, options),
      successNotice: () => ({
        tone: 'green',
        title: 'Follow-up survey recorded',
        message: `${completedSession?.scenarioName || completedSession?.scenarioId || completedSession?.id} follow-up survey recorded for the UAT dashboard.`,
      }),
      refreshes: refreshUatPosture,
    });
    if (response) closeSurveyDrawer();
  }

  const surveyResolveMode = Boolean(
    surveySession && unresolvedFor('uat-survey', surveySession.id),
  );

  // Legacy PM guide results entry points: the embedded admin panel moved to
  // the dedicated /admin/guide-results workspace. Old rail links
  // (?guideResults=1) and notification deep links (?guideAttempt=<id>) still
  // land here, so System Admins are forwarded with the attempt id preserved.
  const legacyGuideAttempt = searchParams.get('guideAttempt');
  if (canViewGuideResults && (searchParams.has('guideResults') || legacyGuideAttempt)) {
    const target = legacyGuideAttempt
      ? `/admin/guide-results?attempt=${encodeURIComponent(legacyGuideAttempt)}`
      : '/admin/guide-results';
    return <Navigate to={target} replace />;
  }

  if (summary.loading || sessions.loading || surveys.loading || onboarding.loading) {
    return <LoadingSkeleton rows={7} />;
  }
  if (summary.error) return <ErrorState error={summary.error} />;
  if (sessions.error) return <ErrorState error={sessions.error} />;
  if (surveys.error) return <ErrorState error={surveys.error} />;
  if (onboarding.error) return <ErrorState error={onboarding.error} />;

  const summaryData = summary.data?.data || {};
  const sourceCounts = summaryData.sourceCounts || null;
  const sessionRows = sessions.data?.data || [];
  const surveyRows = surveys.data?.data || [];
  const checklistRows = onboarding.data?.data || [];
  const personaRows = Array.isArray(summaryData.byPersona) ? summaryData.byPersona : [];
  const cohortRows = Array.isArray(summaryData.byCohort) ? summaryData.byCohort : [];
  const dimensionRows = SURVEY_DIMENSIONS.map((dimension) => ({
    ...dimension,
    stat: averageDimension(surveyRows, dimension.key),
  }));
  const hasDimensionData = dimensionRows.some((dimension) => dimension.stat);
  const satisfactionPercent = Number(summaryData.satisfactionPercent ?? 0);
  const targetPercent = Number(summaryData.targetPercent ?? 80);
  const totalSessions = Number(summaryData.totalSessions ?? 0);
  const completedSessions = Number(summaryData.completedSessions ?? 0);
  const remainingSessions = Math.max(0, totalSessions - completedSessions);
  const completionPercent = totalSessions
    ? Math.round((completedSessions / totalSessions) * 100)
    : 0;
  const satisfactionTone = deriveKpiTone({
    value: satisfactionPercent,
    target: targetPercent,
  });
  const gaugeTone =
    satisfactionPercent >= targetPercent
      ? 'sage'
      : satisfactionPercent >= targetPercent - 10
        ? 'amber'
        : 'brick';
  const heroItems = [
    {
      id: 'satisfaction',
      label: 'Satisfaction',
      value: `${satisfactionPercent}%`,
      status: satisfactionTone,
      footer: responseRatioLabel(summaryData),
    },
    {
      id: 'sessions-complete',
      label: 'Sessions complete',
      value: `${completedSessions}/${totalSessions}`,
      status: deriveKpiTone({ value: completionPercent, target: 100 }),
      statusLabel: `${completionPercent}%`,
      footer: 'Tracked UAT progress',
    },
    {
      id: 'target-state',
      label: 'Target state',
      value: targetStatusLabel(summaryData),
      status: satisfactionTone,
      footer: `Threshold >${targetPercent}%`,
    },
  ];
  const surveyBySessionId = surveyRows.reduce((acc, survey) => {
    const current = acc[survey.sessionId] || {
      count: 0,
      latestSurvey: null,
    };
    const nextLatest =
      !current.latestSurvey || scoreTimestamp(survey) >= scoreTimestamp(current.latestSurvey)
        ? survey
        : current.latestSurvey;
    acc[survey.sessionId] = {
      count: current.count + 1,
      latestSurvey: nextLatest,
    };
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        eyebrow="OWHA Adoption"
        title="UAT Dashboard"
        subtitle="Session completion, satisfaction attainment, and onboarding readiness for bake-off acceptance."
      />
      {actionNotice ? (
        <section
          className={`banner ${actionNotice.tone}`}
          aria-live="polite"
          role={actionNotice.tone === 'red' ? 'alert' : 'status'}
        >
          <div>
            <strong>{actionNotice.title}</strong>
            <p style={{ margin: 0 }}>{actionNotice.message}</p>
          </div>
        </section>
      ) : null}
      {staleRefresh ? (
        <section
          className="banner amber"
          data-testid="uat-stale-refresh-warning"
          aria-live="polite"
          role="status"
        >
          <div className="row spread" style={{ gap: 12, alignItems: 'center' }}>
            <p style={{ margin: 0 }}>
              Your change was saved, but the dashboard display may be stale — the follow-up
              refresh failed.
            </p>
            <button type="button" className="btn outline sm" onClick={retryStaleRefresh}>
              Refresh now
            </button>
          </div>
        </section>
      ) : null}
      <section className="row" style={{ gap: 12, marginBottom: 16 }}>
        <div className="row" style={{ gap: 8 }} role="group" aria-label="UAT dashboard view">
          <button
            type="button"
            className={`btn ${isPulseView ? 'ghost' : 'primary'} sm`}
            data-testid="uat-view-toggle-full"
            onClick={() => setDashboardView('full')}
          >
            Full dashboard
          </button>
          <button
            type="button"
            className={`btn ${isPulseView ? 'primary' : 'ghost'} sm`}
            data-testid="uat-view-toggle-pulse"
            onClick={() => setDashboardView('pulse')}
          >
            Pulse
          </button>
        </div>
        <Link className="btn outline sm" to={buildUatFeedbackReportPath()}>
          Draft UAT feedback report
        </Link>
      </section>
      {sourceCounts ? (
        <div className="muted small" data-testid="uat-source-counts" style={{ marginBottom: 16 }}>
          Reconciled sources: {sourceCounts.sessions || 0} sessions · {sourceCounts.surveys || 0}{' '}
          surveys · {sourceCounts.onboardingChecklists || 0} onboarding checklists ·{' '}
          {sourceCounts.feedbackRecords || 0} feedback records
        </div>
      ) : null}
      {isPulseView ? (
        <div className="uat-redesign" data-testid="uat-pulse-view">
          <KpiHeroBand eyebrow="UAT pulse" items={heroItems} columns={3} />
          <article className="uat-panel">
            <div className="row spread" style={{ gap: 12 }}>
              <h3>
                Cohorts vs the {'>'}
                {targetPercent}% satisfaction line
              </h3>
              <span className="chip outline sm">{cohortRows.length} cohorts</span>
            </div>
            {cohortRows.length === 0 ? (
              <EmptyState
                title="No cohort breakdown found"
                description="Cohort pulse posture will appear here once UAT sessions are recorded."
              />
            ) : (
              <div className="uat-stack">
                {cohortRows.map((cohort) => {
                  const completion = cohortCompletionPercent(cohort);
                  const satisfaction = cohortSatisfactionPercent(cohort);
                  return (
                    <div
                      key={cohort.cohort}
                      className="uat-progress-line"
                      data-testid={`uat-pulse-cohort-${cohort.cohort}`}
                    >
                      <div>
                        <strong>{cohort.cohort}</strong>
                        <div className="muted">
                          {cohort.completedSessionCount ?? 0}/{cohort.sessionCount ?? 0} sessions
                          complete · {completion}%
                        </div>
                        <div className="muted">
                          {cohort.satisfiedResponseCount ?? 0} of {cohort.responseCount ?? 0}{' '}
                          responses scored 4+
                        </div>
                        <div
                          className="muted"
                          data-testid={`uat-pulse-cohort-feedback-${cohort.cohort}`}
                        >
                          {cohort.feedback?.resolved ?? 0} feedback resolved ·{' '}
                          {cohort.feedback?.total ?? 0} tagged
                        </div>
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        {satisfaction == null ? (
                          <span className="chip outline sm">No responses yet</span>
                        ) : (
                          <>
                            <InlineScoreCell
                              value={satisfaction}
                              max={100}
                              suffix="%"
                              tone={bandTone(satisfaction, { good: targetPercent, warn: 50 })}
                              ariaLabel={`${cohort.cohort} satisfaction ${satisfaction}% vs ${targetPercent}% target`}
                            />
                            <StatusChip
                              status={satisfaction >= targetPercent ? 'on_track' : 'at_risk'}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="uat-attainment-note">
              Counts reflect recorded survey responses only — cohorts without responses read “No
              responses yet” rather than being assumed satisfied.
            </p>
          </article>
        </div>
      ) : (
        <>
          <div className="uat-redesign">
            {/* Element-feedback target (route-only entity): the satisfaction
                attainment hero is the >80% SOO proof surface. The framing caption
                conveys "designated Government UAT cohort" without over-claiming in
                the cohort grouping keys (which stay "Wave 1" / "Wave 2"). */}
            <div data-feedback-id="uat-satisfaction-hero" aria-label="UAT satisfaction attainment">
              <KpiHeroBand eyebrow="Adoption posture" items={heroItems} columns={3} />
              <p className="muted small" style={{ marginTop: 8 }}>
                Designated Government UAT cohort — Wave 1 pilot + Wave 2 division leadership
              </p>
            </div>
            <section className="uat-attainment-grid">
              <article className="uat-attainment-panel">
                <div className="row spread">
                  <h3>Satisfaction vs target</h3>
                  <StatusChip
                    status={satisfactionPercent >= targetPercent ? 'on_track' : 'at_risk'}
                  />
                </div>
                <div className="uat-attainment-gauge">
                  <GaugeCard
                    value={satisfactionPercent}
                    min={0}
                    max={100}
                    unit="%"
                    tone={gaugeTone}
                    label="Satisfied responses"
                    sublabel={`Target ${targetPercent}%`}
                    deltaLabel={`${satisfactionPercent - targetPercent >= 0 ? '+' : ''}${satisfactionPercent - targetPercent} pts vs target`}
                    deltaTone={gaugeTone}
                  />
                </div>
                <p className="uat-attainment-note">
                  {Number(summaryData.satisfiedResponseCount ?? 0)} satisfied of{' '}
                  {Number(summaryData.satisfactionResponseCount ?? 0)} scored
                </p>
              </article>
              <article className="uat-attainment-panel">
                <div className="row spread">
                  <h3>Session completion</h3>
                  <span className="chip outline sm">{completionPercent}%</span>
                </div>
                {totalSessions === 0 ? (
                  <EmptyState
                    title="No sessions tracked"
                    description="Completion will appear here once UAT sessions are recorded."
                  />
                ) : (
                  <div className="uat-attainment-donut">
                    <StatusDonutMini
                      size={120}
                      totalLabel="sessions"
                      centerValue={`${completionPercent}%`}
                      ariaLabel="UAT session completion"
                      segments={[
                        { label: 'Complete', count: completedSessions, tone: 'sage' },
                        { label: 'In flight', count: remainingSessions, tone: 'slate' },
                      ]}
                    />
                  </div>
                )}
                <p className="uat-attainment-note">
                  {completedSessions} of {totalSessions} sessions complete
                </p>
              </article>
              <article className="uat-attainment-panel">
                <div className="row spread">
                  <h3>Survey dimensions</h3>
                  <span className="chip outline sm">
                    {surveyRows.length} survey{surveyRows.length === 1 ? '' : 's'}
                  </span>
                </div>
                {!hasDimensionData ? (
                  <EmptyState
                    title="No survey dimensions yet"
                    description="Once follow-up surveys are recorded, the six adoption dimensions will appear here as averaged scores."
                  />
                ) : (
                  <StaggerReveal className="uat-dimensions">
                    {dimensionRows.map((dimension) => (
                      <div key={dimension.key} className="uat-dimension-row">
                        <div className="uat-dimension-label">
                          <strong>{dimension.label}</strong>
                          <span className="muted">{dimension.hint}</span>
                        </div>
                        <div className="uat-dimension-score">
                          {dimension.stat ? (
                            <>
                              <InlineScoreCell
                                value={Number(dimension.stat.average.toFixed(1))}
                                max={5}
                                suffix="/5"
                                tone={bandTone(dimension.stat.average, DIMENSION_THRESHOLDS)}
                                ariaLabel={`${dimension.label}: ${dimension.stat.average.toFixed(1)} of 5 average`}
                              />
                              <span className="muted" style={{ fontSize: '0.78rem' }}>
                                {dimension.stat.count} response
                                {dimension.stat.count === 1 ? '' : 's'}
                              </span>
                            </>
                          ) : (
                            <span className="muted" style={{ fontSize: '0.82rem' }}>
                              No responses yet
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </StaggerReveal>
                )}
              </article>
            </section>
            <article className="uat-panel">
              <div className="row spread" style={{ gap: 12 }}>
                <h3>Cohort readiness</h3>
                <span className="chip outline sm">{cohortRows.length} cohorts</span>
              </div>
              {cohortRows.length === 0 ? (
                <EmptyState
                  title="No cohort breakdown found"
                  description="Per-cohort completion and satisfaction posture will appear here once UAT sessions are recorded."
                />
              ) : (
                <StaggerReveal className="uat-cohort-stack">
                  {cohortRows.map((cohort) => {
                    const completion = cohortCompletionPercent(cohort);
                    const satisfaction = cohortSatisfactionPercent(cohort);
                    return (
                      <div key={cohort.cohort} className="uat-cohort-row">
                        <div className="uat-cohort-id">
                          <strong>{cohort.cohort}</strong>
                          <span className="muted">
                            {cohort.completedSessionCount ?? 0}/{cohort.sessionCount ?? 0} sessions
                          </span>
                        </div>
                        <div className="uat-cohort-metrics">
                          <div className="uat-cohort-metric">
                            <span>Completion</span>
                            <InlineScoreCell
                              value={completion}
                              max={100}
                              suffix="%"
                              tone={bandTone(completion, { good: 100, warn: 50 })}
                              ariaLabel={`${cohort.cohort} completion ${completion}%`}
                            />
                          </div>
                          <div className="uat-cohort-metric">
                            <span>Satisfaction</span>
                            {satisfaction == null ? (
                              <span className="muted" style={{ fontSize: '0.82rem' }}>
                                No responses
                              </span>
                            ) : (
                              <InlineScoreCell
                                value={satisfaction}
                                max={100}
                                suffix="%"
                                tone={bandTone(satisfaction, { good: targetPercent, warn: 50 })}
                                ariaLabel={`${cohort.cohort} satisfaction ${satisfaction}%`}
                              />
                            )}
                          </div>
                        </div>
                        <div className="uat-cohort-chips">
                          <span className="chip outline sm">
                            {cohort.satisfiedResponseCount ?? 0}/{cohort.responseCount ?? 0} scored
                            4+
                          </span>
                          <span
                            className="chip outline sm"
                            data-testid={`uat-cohort-feedback-${cohort.cohort}`}
                          >
                            {cohort.feedback?.resolved ?? 0} of {cohort.feedback?.total ?? 0}{' '}
                            feedback resolved
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </StaggerReveal>
              )}
            </article>
          </div>
          <section className="uat-grid">
            <article className="uat-panel">
              <div className="row spread" style={{ gap: 12 }}>
                <h3>UAT Sessions</h3>
                <span className="chip outline sm">{sessionRows.length} sessions</span>
              </div>
              {sessionRows.length === 0 ? (
                <EmptyState
                  title="No UAT sessions found"
                  description="Planned and active adoption sessions will appear here with scenario completion and blockers."
                />
              ) : (
                <div className="uat-stack">
                  {sessionRows.map((session) => (
                    <div key={session.id} className="uat-progress-line">
                      <div>
                        <strong>{session.scenarioName || session.scenarioId || session.id}</strong>
                        <div className="muted">
                          {session.cohort || 'Unassigned cohort'} · {session.completionPercent ?? 0}
                          %
                        </div>
                        <div className="muted">
                          {surveyBySessionId[session.id]?.count ?? 0} responses
                        </div>
                        {surveyBySessionId[session.id]?.latestSurvey ? (
                          <div className="muted">
                            Latest {surveyBySessionId[session.id]?.latestSurvey?.overallScore ?? 0}
                            /5
                          </div>
                        ) : null}
                        <div className="feedback-ops-context uat-session-context">
                          <section>
                            <strong>Primary user</strong>
                            <p>
                              {[
                                session.userDisplayName || session.userId || 'Unassigned user',
                                session.userRole,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </section>
                          <section>
                            <strong>Participants</strong>
                            <p>{participantSummary(session)}</p>
                          </section>
                          <section>
                            <strong>Session window</strong>
                            <p>{sessionWindowLabel(session)}</p>
                          </section>
                          <section>
                            <strong>Follow-up</strong>
                            <p>
                              {session.followUpRequired
                                ? 'Follow-up required'
                                : 'No follow-up required'}
                            </p>
                          </section>
                          <section>
                            <strong>Issues encountered</strong>
                            <p>{issuesSummary(session)}</p>
                          </section>
                          <section>
                            <strong>Facilitator</strong>
                            <p>
                              {personLabel(
                                session.facilitator,
                                session.facilitatorUserId || 'Facilitator pending',
                              )}
                            </p>
                          </section>
                          <section>
                            <strong>Facilitator notes</strong>
                            <p>
                              {session.facilitatorNotes ||
                                session.notes ||
                                'No facilitator notes recorded.'}
                            </p>
                          </section>
                        </div>
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <StatusChip status={session.status || 'scheduled'} />
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => markSessionComplete(session)}
                          disabled={mutation.running || session.status === 'complete'}
                        >
                          {unresolvedFor('uat-session-complete', session.id)
                            ? 'Resolve previous submission'
                            : 'Mark session complete'}
                        </button>
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => openSurveyDrawer(session)}
                          disabled={mutation.running || surveyResolveMode}
                        >
                          Add follow-up survey
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
            <article className="uat-panel">
              <div className="row spread" style={{ gap: 12 }}>
                <h3>Onboarding Checklists</h3>
                <span className="chip outline sm">{checklistRows.length} personas</span>
              </div>
              {checklistRows.length === 0 ? (
                <EmptyState
                  title="No onboarding checklists found"
                  description="Persona-specific readiness and training completion will appear here."
                />
              ) : (
                <div className="uat-stack">
                  {checklistRows.map((checklist) => (
                    <div key={checklist.id} className="uat-progress-line">
                      <div>
                        <strong>{checklist.persona}</strong>
                        <div className="muted">{checklist.completionPercent ?? 0}% complete</div>
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <StatusChip status={checklist.status || 'not_started'} />
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => markComplete(checklist)}
                          disabled={mutation.running || checklist.status === 'complete'}
                        >
                          {unresolvedFor('onboarding-complete', checklist.id)
                            ? 'Resolve previous submission'
                            : 'Mark complete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
            <article className="uat-panel">
              <div className="row spread" style={{ gap: 12 }}>
                <h3>Persona readiness</h3>
                <span className="chip outline sm">{personaRows.length} personas</span>
              </div>
              {personaRows.length === 0 ? (
                <EmptyState
                  title="No persona rollups found"
                  description="Persona-based adoption, onboarding, and response posture will appear here once UAT activity is recorded."
                />
              ) : (
                <div className="uat-stack">
                  {personaRows.map((persona) => (
                    <div key={persona.persona} className="uat-progress-line">
                      <div>
                        <strong>{persona.persona}</strong>
                        <div className="muted">{persona.satisfactionPercent ?? 0}% satisfied</div>
                        <div className="muted">
                          {persona.responseCount ?? 0} response
                          {Number(persona.responseCount ?? 0) === 1 ? '' : 's'}
                        </div>
                        <div className="muted">
                          {persona.onboardingCompletionPercent ?? 0}% onboarding complete
                        </div>
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="chip outline sm">
                          {persona.completedSessionCount ?? 0}/{persona.sessionCount ?? 0} sessions
                          complete
                        </span>
                        <StatusChip status={persona.onboardingStatus || 'not_started'} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </section>
        </>
      )}
      <Drawer
        open={Boolean(surveySession)}
        onClose={closeSurveyDrawer}
        title="Record follow-up survey"
        width={560}
      >
        {/* surveyResolveMode is computed at render so a drawer reopened after a
            close (or a reload with a stored pending mutation) stays fenced. */}
        <DrawerForm onSubmit={submitFollowUpSurvey} submitting={mutation.running}>
          {surveyResolveMode ? (
            <section className="banner amber" role="status" style={{ marginBottom: 12 }}>
              <p style={{ margin: 0 }}>
                A previous survey submission for this session couldn&apos;t be confirmed. The
                original answers are locked below — Resolve previous submission replays them
                safely; it can never record the survey twice.
              </p>
            </section>
          ) : prefilledSurvey ? (
            <section className="banner blue" role="status" style={{ marginBottom: 12 }}>
              <p style={{ margin: 0 }}>
                Prefilled from the latest recorded survey. Review or update the answers before
                recording another response for this session.
              </p>
            </section>
          ) : null}
          <fieldset
            disabled={surveyResolveMode}
            style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
          >
          <div style={{ display: 'grid', gap: 16 }}>
            <section className="feedback-ops-context">
              <section>
                <strong>Scenario</strong>
                <p>
                  {surveySession?.scenarioName ||
                    surveySession?.scenarioId ||
                    surveySession?.id ||
                    'Survey draft'}
                </p>
              </section>
              <section>
                <strong>Cohort</strong>
                <p>{surveySession?.cohort || 'Unassigned cohort'}</p>
              </section>
            </section>
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              }}
            >
              <FormField
                label="Overall satisfaction"
                htmlFor="survey-overall-score"
                required
                error={surveyErrors.overallScore}
              >
                <Select
                  id="survey-overall-score"
                  value={surveyDraft.overallScore}
                  onChange={(value) => updateSurveyField('overallScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
              <FormField
                label="Navigation ease"
                htmlFor="survey-navigation-score"
                required
                error={surveyErrors.navigationEaseScore}
              >
                <Select
                  id="survey-navigation-score"
                  value={surveyDraft.navigationEaseScore}
                  onChange={(value) => updateSurveyField('navigationEaseScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
              <FormField
                label="Workflow completion"
                htmlFor="survey-workflow-score"
                required
                error={surveyErrors.workflowCompletionScore}
              >
                <Select
                  id="survey-workflow-score"
                  value={surveyDraft.workflowCompletionScore}
                  onChange={(value) => updateSurveyField('workflowCompletionScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
              <FormField
                label="Confidence in data"
                htmlFor="survey-data-confidence"
                required
                error={surveyErrors.dataConfidenceScore}
              >
                <Select
                  id="survey-data-confidence"
                  value={surveyDraft.dataConfidenceScore}
                  onChange={(value) => updateSurveyField('dataConfidenceScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
              <FormField
                label="Reporting usefulness"
                htmlFor="survey-reporting-score"
                required
                error={surveyErrors.reportingUsefulnessScore}
              >
                <Select
                  id="survey-reporting-score"
                  value={surveyDraft.reportingUsefulnessScore}
                  onChange={(value) => updateSurveyField('reportingUsefulnessScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
              <FormField
                label="AI usefulness"
                htmlFor="survey-ai-score"
                required
                error={surveyErrors.aiUsefulnessScore}
              >
                <Select
                  id="survey-ai-score"
                  value={surveyDraft.aiUsefulnessScore}
                  onChange={(value) => updateSurveyField('aiUsefulnessScore', value)}
                  options={SURVEY_SCORE_OPTIONS}
                  placeholder="Select a score"
                  disabled={mutation.running || surveyResolveMode}
                />
              </FormField>
            </div>
            <FormField
              label="Operational-use intent"
              htmlFor="survey-operational-intent"
              required
              error={surveyErrors.operationalUseIntent}
            >
              <Select
                id="survey-operational-intent"
                value={surveyDraft.operationalUseIntent}
                onChange={(value) => updateSurveyField('operationalUseIntent', value)}
                options={SURVEY_INTENT_OPTIONS}
                placeholder="Select intent"
                disabled={mutation.running || surveyResolveMode}
              />
            </FormField>
            <FormField
              label="Biggest blocker"
              htmlFor="survey-biggest-blocker"
              required
              error={surveyErrors.biggestBlocker}
            >
              <Textarea
                id="survey-biggest-blocker"
                value={surveyDraft.biggestBlocker}
                onChange={(value) => updateSurveyField('biggestBlocker', value)}
                rows={3}
                placeholder="Summarize the main blocker still affecting operational confidence."
                disabled={mutation.running || surveyResolveMode}
              />
            </FormField>
            <FormField
              label="Most valuable feature"
              htmlFor="survey-most-valuable-feature"
              required
              error={surveyErrors.mostValuableFeature}
            >
              <Textarea
                id="survey-most-valuable-feature"
                value={surveyDraft.mostValuableFeature}
                onChange={(value) => updateSurveyField('mostValuableFeature', value)}
                rows={3}
                placeholder="Capture the feature the participant found most valuable."
                disabled={mutation.running || surveyResolveMode}
              />
            </FormField>
            <FormField
              label="Free-text comments"
              htmlFor="survey-comments"
              required
              error={surveyErrors.comments}
            >
              <Textarea
                id="survey-comments"
                value={surveyDraft.comments}
                onChange={(value) => updateSurveyField('comments', value)}
                rows={4}
                placeholder="Capture any additional adoption or workflow feedback."
                disabled={mutation.running || surveyResolveMode}
              />
            </FormField>
          </div>
          </fieldset>
          <DrawerFormActions
            onCancel={closeSurveyDrawer}
            submitLabel={surveyResolveMode ? 'Resolve previous submission' : 'Record survey'}
            submitting={mutation.running}
          />
        </DrawerForm>
      </Drawer>
    </>
  );
}
